import { Dropdown, Typography } from "@hellocarbo/lasagne";
import CopyToClipboardButton from "@ui/CopyToClipboardButton";
import { use } from "react";
import { useTranslation } from "react-i18next";

import FlyingCat from "@/assets/common/other_icons/flying-cat.svg?react";
import { UserContext } from "@/contexts";
import { PortalContainerContext } from "@/contexts";
import { TReviewSharingStatus } from "@/types";

import BaseCard, { ActionButton } from "./BaseCard";

type SharingStatusProps = {
  status: TReviewSharingStatus;
  url?: string;
  updateSharingStatus: (status: TReviewSharingStatus) => void;
};

function SharingStatus({
  status,
  url,
  updateSharingStatus,
}: SharingStatusProps) {
  const { t } = useTranslation(["communicate", "formatter"]);
  const { container } = use(PortalContainerContext);

  const statusOptions = Object.values(TReviewSharingStatus).map((value) => ({
    label: t(`communicate:sharingStatus.status.${value}`),
    value,
  }));
  const { user } = use(UserContext);
  const isFreeTrial = user?.company.subscriptionType === "start";

  return (
    <BaseCard
      Icon={FlyingCat}
      title={t("communicate:sharingStatus.title")}
      text={t("communicate:sharingStatus.text")}
    >
      <div className="mt-lg gap-y-lg flex flex-col">
        <div>
          {!isFreeTrial && (
            <Typography step="-1" className="text-grayscale">
              {t("communicate:sharingStatus.modifyPublicFootprintState")}
            </Typography>
          )}
          <div className="mt-3xs w-[265px]">
            {isFreeTrial ? (
              <ActionButton>
                {t("communicate:sharingStatus.freeTrialCta")}
              </ActionButton>
            ) : (
              <Dropdown
                key={status}
                portalContainer={container as Element}
                groups={[{ items: statusOptions }]}
                defaultValue={status}
                onChange={(status) =>
                  updateSharingStatus(status as TReviewSharingStatus)
                }
              />
            )}
          </div>
        </div>
        {status !== TReviewSharingStatus.private && url && (
          <div>
            <Typography step="-1" className="mb-3xs text-grayscale">
              {t("communicate:sharingStatus.copyLink")}
            </Typography>
            <CopyToClipboardButton
              url={url}
              toastProps={{
                title: t("communicate:sharingStatus.linkCopied"),
                toastType: "simple",
                intent: "positive",
              }}
            />
          </div>
        )}
      </div>
    </BaseCard>
  );
}

export default SharingStatus;
